import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '../../components/ui/Button'
import { apiFetch, ApiError } from '../../lib/api-client'

interface Share { renter_id: string; name: string; role: string | null; share_bps: number }
interface Eligible { renter_id: string; name: string; role: string }
interface SharesResponse { lease_id: string; total_bps: number; shares: Share[]; eligible: Eligible[] }
interface Split { renter_id: string; name: string; share_bps: number; amount_oere: number }
interface SplitResponse { invoice_id: string; amount_oere: number; splits: Split[] }
interface InvoiceItem { id: string; title: string; invoice_number: string | null; amount_oere: number }

const BPS_TOTAL = 10000

// Øre → "1.234,56 kr." (display only; amounts always come from the API).
function kr(oere: number): string {
  return (oere / 100).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' kr.'
}

// Basispoint → "33,34" using integer arithmetic only.
function bpsToText(bps: number): string {
  const whole = Math.trunc(bps / 100)
  const frac = bps % 100
  return `${whole},${String(frac).padStart(2, '0')}`
}

const PERCENT_RE = /^(\d{1,3})(?:,(\d{1,2}))?$/

// "33,34" → 3334. Empty → 0. Anything else (point, thousand separators, >2 decimals) → null.
function parsePercent(raw: string): number | null {
  const t = raw.trim()
  if (t === '') return 0
  const m = PERCENT_RE.exec(t)
  if (!m) return null
  const bps = parseInt(m[1], 10) * 100 + (m[2] ? parseInt(m[2].padEnd(2, '0'), 10) : 0)
  return bps > BPS_TOTAL ? null : bps
}

function roleLabel(role: string | null): string {
  if (role === 'primary_tenant') return 'Hovedlejer'
  if (role === 'co_tenant') return 'Medlejer'
  return '—'
}

const th: React.CSSProperties = { padding: '10px 20px', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'left' }
const td: React.CSSProperties = { padding: '11px 20px', fontSize: 13, color: '#111827', borderTop: '1px solid #f3f4f6', overflowWrap: 'anywhere' }
const num: React.CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }
const headingStyle: React.CSSProperties = { fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }
const card: React.CSSProperties = { background: '#fff', border: '1px solid #e8eaed', borderRadius: 10, marginTop: 20 }
const inputStyle: React.CSSProperties = {
  width: 96, height: 32, padding: '0 10px', borderRadius: 7, border: '1px solid #e8ecf4', fontSize: 13,
  color: '#0f172a', background: '#fff', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none',
  textAlign: 'right', fontVariantNumeric: 'tabular-nums',
}

export function RentSharesSection({ leaseId }: { leaseId: string }) {
  // key resets all state when the lease changes
  return <RentSharesInner key={leaseId} leaseId={leaseId} />
}

function RentSharesInner({ leaseId }: { leaseId: string }) {
  const base = `/api/v1/leases/${leaseId}/rent-shares`
  const [data, setData] = useState<SharesResponse | null>(null)
  const [loadFailed, setLoadFailed] = useState(false)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [justSaved, setJustSaved] = useState(false)

  const [invoices, setInvoices] = useState<InvoiceItem[] | null>(null)
  const [invoicesFailed, setInvoicesFailed] = useState(false)
  const [invoiceId, setInvoiceId] = useState('')
  const [splitRes, setSplitRes] = useState<{ key: string; split: SplitResponse | null; failed: 'unsplittable' | 'failed' | null } | null>(null)

  const apply = useCallback((r: SharesResponse) => {
    const d: Record<string, string> = {}
    for (const e of r.eligible) d[e.renter_id] = ''
    for (const s of r.shares) d[s.renter_id] = bpsToText(s.share_bps)
    setData(r)
    setDraft(d)
  }, [])

  useEffect(() => {
    let live = true
    apiFetch<SharesResponse>(base)
      .then((r) => { if (live) { setLoadFailed(false); apply(r) } })
      .catch(() => { if (live) setLoadFailed(true) })
    return () => { live = false }
  }, [base, apply])

  useEffect(() => {
    let live = true
    apiFetch<{ items: InvoiceItem[] }>(`/api/v1/invoices?lease_id=${leaseId}&limit=250`)
      .then((r) => {
        if (!live) return
        setInvoices(r.items)
        setInvoiceId((cur) => cur || (r.items[0]?.id ?? ''))
      })
      .catch(() => { if (live) { setInvoices([]); setInvoicesFailed(true) } })
    return () => { live = false }
  }, [leaseId])

  const savedKey = data ? data.shares.map((s) => `${s.renter_id}:${s.share_bps}`).join('|') : ''
  const hasSaved = (data?.shares.length ?? 0) > 0

  // Per-invoice split from the backend. Re-fetched whenever the saved shares change.
  const splitKey = invoiceId && hasSaved ? `${invoiceId}#${savedKey}` : ''
  useEffect(() => {
    if (!splitKey) return
    let live = true
    apiFetch<SplitResponse>(`${base}/invoices/${invoiceId}`)
      .then((r) => { if (live) setSplitRes({ key: splitKey, split: r, failed: null }) })
      .catch((e) => {
        if (live) setSplitRes({ key: splitKey, split: null, failed: e instanceof ApiError && e.status === 422 ? 'unsplittable' : 'failed' })
      })
    return () => { live = false }
  }, [base, invoiceId, splitKey])
  const current = splitKey && splitRes?.key === splitKey ? splitRes : null
  const split = current?.split ?? null
  const splitState: 'idle' | 'loading' | 'unsplittable' | 'failed' =
    !splitKey ? 'idle' : !current ? 'loading' : (current.failed ?? 'idle')

  const eligible = useMemo(() => data?.eligible ?? [], [data])

  const parsed = useMemo(() => {
    const bps: Record<string, number> = {}
    const invalid: Record<string, boolean> = {}
    for (const e of eligible) {
      const v = parsePercent(draft[e.renter_id] ?? '')
      if (v === null) invalid[e.renter_id] = true
      else bps[e.renter_id] = v
    }
    return { bps, invalid }
  }, [eligible, draft])

  if (loadFailed) {
    return (
      <div style={card}>
        <div style={{ padding: '14px 24px', borderBottom: '1px solid #e8eaed' }}><span style={headingStyle}>Fordeling mellem medlejere</span></div>
        <p role="alert" style={{ padding: '14px 24px', margin: 0, fontSize: 13, color: '#dc2626' }}>
          Kunne ikke hente fordelingen. Genindlæs siden og prøv igen.
        </p>
      </div>
    )
  }
  if (data === null) {
    return (
      <div style={card}>
        <div style={{ padding: '14px 24px', borderBottom: '1px solid #e8eaed' }}><span style={headingStyle}>Fordeling mellem medlejere</span></div>
        <p style={{ padding: '14px 24px', margin: 0, fontSize: 13, color: '#9ca3af' }}>Henter fordeling…</p>
      </div>
    )
  }

  const savedBps: Record<string, number> = {}
  for (const s of data.shares) savedBps[s.renter_id] = s.share_bps

  const anyInvalid = Object.keys(parsed.invalid).length > 0
  const sumBps = Object.values(parsed.bps).reduce((a, b) => a + b, 0)
  const staleShares = data.shares.filter((s) => !eligible.some((e) => e.renter_id === s.renter_id))
  // Stale saved shares (people no longer on the active contract) count as a change so they can be cleared.
  const dirty = staleShares.length > 0
    || eligible.some((e) => (parsed.bps[e.renter_id] ?? 0) !== (savedBps[e.renter_id] ?? 0))
  const clearing = sumBps === 0 && !anyInvalid
  const sumOk = !anyInvalid && (sumBps === BPS_TOTAL || (clearing && hasSaved))

  let validationMessage: string | null = null
  if (anyInvalid) validationMessage = 'Skriv andelen som procent med komma og højst to decimaler, fx 33,34.'
  else if (sumBps !== BPS_TOTAL && !(clearing && hasSaved) && dirty) {
    validationMessage = clearing
      ? 'Angiv en andel til mindst én medlejer.'
      : `Andelene skal i alt udgøre 100,00 %. Nu: ${bpsToText(sumBps)} %.`
  }

  const setValue = (id: string, v: string) => {
    setDraft((prev) => ({ ...prev, [id]: v }))
    setJustSaved(false)
    setError(null)
  }

  const reset = () => { apply(data); setError(null); setJustSaved(false) }

  const save = async () => {
    setError(null)
    setSaving(true)
    try {
      const shares = eligible
        .filter((e) => (parsed.bps[e.renter_id] ?? 0) > 0)
        .map((e) => ({ renter_id: e.renter_id, share_bps: parsed.bps[e.renter_id] }))
      const updated = await apiFetch<SharesResponse>(base, { method: 'PUT', body: JSON.stringify({ shares }) })
      apply(updated)
      setJustSaved(true)
    } catch (e) {
      // apiFetch flattens non-string (list) details to "field: msg" or 'Request failed' — only show real string details.
      const detail = e instanceof ApiError && e.status === 422 ? e.message : null
      const isStringDetail = !!detail && detail !== 'Request failed' && !/^[\w.]+: /.test(detail)
      setError(isStringDetail ? detail : e instanceof ApiError && e.status === 422
        ? 'Kunne ikke gemme fordelingen. Tjek andelene og prøv igen.'
        : 'Kunne ikke gemme fordelingen. Prøv igen.')
    } finally {
      setSaving(false)
    }
  }

  const splitFor = (renterId: string): Split | undefined => split?.splits.find((s) => s.renter_id === renterId)
  const selectedInvoice = invoices?.find((i) => i.id === invoiceId)

  const header = (
    <div style={{ padding: '14px 24px', borderBottom: '1px solid #e8eaed', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
      <span style={headingStyle}>Fordeling mellem medlejere</span>
      {eligible.length > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {justSaved && !dirty && <span style={{ fontSize: 12, color: '#16a34a' }}>Gemt</span>}
          <Button size="sm" variant="secondary" onClick={reset} disabled={!dirty || saving}>Nulstil</Button>
          <Button size="sm" onClick={save} isLoading={saving} disabled={!dirty || !sumOk}>Gem fordeling</Button>
        </div>
      )}
    </div>
  )

  if (eligible.length === 0) {
    return (
      <div style={card}>
        {header}
        <p style={{ padding: '14px 24px', margin: 0, fontSize: 13, color: '#94a3b8' }}>
          Lejemålet har ingen aktiv kontrakt med medlejere.
        </p>
      </div>
    )
  }

  const sumColor = anyInvalid || validationMessage ? '#dc2626' : sumBps === BPS_TOTAL ? '#15803d' : '#6b7280'

  return (
    <div style={card}>
      {header}
      <p style={{ padding: '12px 24px 0', margin: 0, fontSize: 12, color: '#64748b' }}>
        Fordelingen er kun en visning. Opkrævning og restance sker fortsat på lejemålet.
      </p>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 8 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={th}>Medlejer</th>
              <th style={th}>Rolle</th>
              <th style={{ ...th, ...num }}>Andel</th>
              <th style={{ ...th, ...num }}>Beløb pr. opkrævning</th>
            </tr>
          </thead>
          <tbody>
            {eligible.map((e) => {
              const bad = !!parsed.invalid[e.renter_id]
              const s = splitFor(e.renter_id)
              return (
                <tr key={e.renter_id}>
                  <td style={td}>{e.name}</td>
                  <td style={{ ...td, color: '#6b7280' }}>{roleLabel(e.role)}</td>
                  <td style={{ ...td, ...num }}>
                    <input
                      type="text" inputMode="decimal" aria-label={`Andel for ${e.name}`} aria-invalid={bad}
                      value={draft[e.renter_id] ?? ''} placeholder="0,00"
                      onChange={(ev) => setValue(e.renter_id, ev.target.value)}
                      style={{ ...inputStyle, borderColor: bad ? '#dc2626' : '#e8ecf4' }}
                    />{' '}
                    <span style={{ color: '#6b7280' }}>%</span>
                  </td>
                  <td style={{ ...td, ...num }}>{s ? kr(s.amount_oere) : '—'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div style={{ padding: '10px 24px 14px' }}>
        <div data-testid="rent-shares-sum" style={{ fontSize: 13, fontWeight: 600, color: sumColor, fontVariantNumeric: 'tabular-nums' }}>
          {anyInvalid ? 'Sum: ugyldig andel' : `Sum: ${bpsToText(sumBps)} %`}
        </div>
        {validationMessage && <p role="alert" style={{ fontSize: 12, color: '#dc2626', margin: '6px 0 0' }}>{validationMessage}</p>}
        {error && <p role="alert" style={{ fontSize: 12, color: '#dc2626', margin: '6px 0 0' }}>{error}</p>}
        {staleShares.length > 0 && (
          <p style={{ fontSize: 12, color: '#b45309', margin: '6px 0 0' }}>
            Gemt fordeling indeholder personer, der ikke længere er hovedlejer eller medlejer på den aktive kontrakt: {staleShares.map((s) => s.name).join(', ')}. Gem en ny fordeling for at rette det.
          </p>
        )}
        {dirty && hasSaved && (
          <p style={{ fontSize: 12, color: '#94a3b8', margin: '6px 0 0' }}>Beløbene herunder viser den gemte fordeling.</p>
        )}
      </div>

      <div style={{ borderTop: '1px solid #e8eaed', padding: '14px 24px' }}>
        <label htmlFor="rent-shares-invoice" style={{ ...headingStyle, display: 'block', marginBottom: 8 }}>Opkrævning</label>
        {invoices === null ? (
          <p style={{ margin: 0, fontSize: 13, color: '#9ca3af' }}>Henter opkrævninger…</p>
        ) : invoicesFailed ? (
          <p role="alert" style={{ margin: 0, fontSize: 13, color: '#dc2626' }}>Kunne ikke hente opkrævninger. Genindlæs siden og prøv igen.</p>
        ) : invoices.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: '#94a3b8' }}>Der er ingen opkrævninger på lejemålet endnu.</p>
        ) : (
          <select
            id="rent-shares-invoice" value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)}
            style={{ width: '100%', maxWidth: 520, height: 36, padding: '0 10px', borderRadius: 7, border: '1px solid #e8ecf4', fontSize: 13, color: '#0f172a', background: '#fff', fontFamily: 'inherit', boxSizing: 'border-box' }}
          >
            {invoices.map((i) => (
              <option key={i.id} value={i.id}>
                {i.title}{i.invoice_number ? ` (${i.invoice_number})` : ''} · {kr(i.amount_oere)}
              </option>
            ))}
          </select>
        )}

        {invoices !== null && invoices.length >= 250 && (
          <p style={{ margin: '8px 0 0', fontSize: 12, color: '#94a3b8' }}>Viser de seneste 250 opkrævninger.</p>
        )}
        {selectedInvoice && !hasSaved && (
          <p style={{ margin: '12px 0 0', fontSize: 13, color: '#94a3b8' }}>
            Gem en fordeling for at se, hvad hver medlejers andel udgør af opkrævningen.
          </p>
        )}
        {selectedInvoice && hasSaved && splitState === 'loading' && (
          <p style={{ margin: '12px 0 0', fontSize: 13, color: '#9ca3af' }}>Henter fordeling…</p>
        )}
        {selectedInvoice && hasSaved && splitState === 'unsplittable' && (
          <p style={{ margin: '12px 0 0', fontSize: 13, color: '#6b7280' }}>Kan ikke fordeles</p>
        )}
        {selectedInvoice && hasSaved && splitState === 'failed' && (
          <p role="alert" style={{ margin: '12px 0 0', fontSize: 13, color: '#dc2626' }}>Kunne ikke hente fordelingen af opkrævningen. Prøv igen.</p>
        )}
        {split && splitState === 'idle' && (
          <div style={{ overflowX: 'auto', marginTop: 12, border: '1px solid #f3f4f6', borderRadius: 8 }}>
            <table data-testid="rent-shares-split" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  <th style={th}>Medlejer</th>
                  <th style={{ ...th, ...num }}>Andel</th>
                  <th style={{ ...th, ...num }}>Beløb</th>
                </tr>
              </thead>
              <tbody>
                {split.splits.map((s) => (
                  <tr key={s.renter_id}>
                    <td style={td}>{s.name}</td>
                    <td style={{ ...td, ...num }}>{bpsToText(s.share_bps)} %</td>
                    <td style={{ ...td, ...num }}>{kr(s.amount_oere)}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...td, fontWeight: 600 }}>I alt</td>
                  <td style={{ ...td, ...num }} />
                  <td style={{ ...td, ...num, fontWeight: 600 }}>{kr(split.amount_oere)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
